import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../context/AppContext';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, Filler);

const AnalyticsDashboard = () => {
    const { t } = useTranslation();
    const { authFetch } = useContext(AppContext);
    
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            const res = await authFetch('/api/analytics?type=dashboard');
            if (res.ok) {
                const data = await res.json();
                setAnalytics(data);
            }
        } catch (err) {
            console.error("Failed to fetch analytics:", err);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (val) => {
        const f = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(val || 0);
        return `${f} CFA`;
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[50vh] space-y-3">
                <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-gray-500 text-sm font-medium">{t('Loading analytics data...')}</p>
            </div>
        );
    }

    if (!analytics) return <div className="p-8 text-center text-red-600 font-medium">Failed to load analytics data.</div>;

    // 1. Forecasting Chart Data
    const generateForecastChart = () => {
        const labels = [];
        const histData = [];
        const predData = [];
        
        analytics.historical?.forEach(h => {
            labels.push(h.date);
            histData.push(parseFloat(h.daily_revenue));
            predData.push(null);
        });
        
        if (analytics.historical?.length > 0 && analytics.forecast?.length > 0) {
            const lastHist = analytics.historical[analytics.historical.length - 1];
            predData[predData.length - 1] = parseFloat(lastHist.daily_revenue);
        }

        analytics.forecast?.forEach(f => {
            labels.push(f.date);
            histData.push(null);
            predData.push(parseFloat(f.predicted_revenue));
        });

        return {
            labels,
            datasets: [
                {
                    label: t('Historical Revenue'),
                    data: histData,
                    borderColor: '#4f46e5',
                    backgroundColor: 'rgba(79, 70, 229, 0.08)',
                    tension: 0.2,
                    fill: true
                },
                {
                    label: t('Projected Revenue (30 Days)'),
                    data: predData,
                    borderColor: '#0284c7',
                    borderDash: [4, 4],
                    backgroundColor: 'rgba(2, 132, 199, 0.05)',
                    tension: 0.2,
                    fill: true
                }
            ]
        };
    };

    // 2. Branch Comparison Chart Data
    const generateBranchChart = () => {
        return {
            labels: analytics.branch_comparison?.map(b => b.branch_name) || [],
            datasets: [
                {
                    label: t('Gross Revenue'),
                    data: analytics.branch_comparison?.map(b => parseFloat(b.total_revenue)),
                    backgroundColor: '#4f46e5',
                },
                {
                    label: t('Total Expenses'),
                    data: analytics.branch_comparison?.map(b => parseFloat(b.total_expenses)),
                    backgroundColor: '#e11d48',
                },
                {
                    label: t('Net Gain'),
                    data: analytics.branch_comparison?.map(b => parseFloat(b.net_performance)),
                    backgroundColor: '#10b981',
                }
            ]
        };
    };

    const b = analytics.breakeven;
    const progressPct = b ? Math.min(100, Math.max(0, (b.current_revenue_30d / (b.break_even_point || 1)) * 100)) : 0;

    return (
        <div className="space-y-6 max-w-7xl mx-auto pb-16">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">{t('Business Analytics & Forecasting')}</h1>
                    <p className="text-gray-500 text-sm mt-1">{t('Break-even analysis, revenue projections, and branch performance.')}</p>
                </div>
            </div>

            {/* Break-Even Analysis Card */}
            {b && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-base font-bold text-gray-900 mb-1">{t('30-Day Break-Even Analysis')}</h2>
                    <p className="text-xs text-gray-500 mb-6 max-w-2xl">{t('Formula: Fixed Overheads / Gross Margin %. Calculates the minimum revenue needed across operations to cover operating costs.')}</p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('Fixed Overheads')}</p>
                            <p className="text-xl font-bold text-gray-900">{formatCurrency(b.fixed_costs)}</p>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('Avg Gross Margin')}</p>
                            <p className="text-xl font-bold text-indigo-600">{b.gross_margin_pct.toFixed(1)}%</p>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('Current Month Revenue')}</p>
                            <p className={`text-xl font-bold ${b.status === 'Profitable' ? 'text-emerald-600' : 'text-amber-600'}`}>{formatCurrency(b.current_revenue_30d)}</p>
                        </div>
                        <div className="p-4 bg-indigo-50/60 rounded-lg border border-indigo-100">
                            <p className="text-xs font-semibold text-indigo-800 uppercase tracking-wider mb-1">{t('Required Target')}</p>
                            <p className="text-xl font-bold text-indigo-900">{formatCurrency(b.break_even_point)}</p>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="pt-2">
                        <div className="flex mb-2 items-center justify-between text-xs">
                            <span className={`px-2.5 py-0.5 rounded font-semibold ${b.status === 'Profitable' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                                {b.status}
                            </span>
                            <span className="font-semibold text-gray-700">
                                {progressPct.toFixed(1)}% {t('to Target')}
                            </span>
                        </div>
                        <div className="overflow-hidden h-2.5 rounded-full bg-gray-100">
                            <div 
                                style={{ width: `${progressPct}%` }} 
                                className={`h-full rounded-full ${b.status === 'Profitable' ? 'bg-emerald-500' : 'bg-amber-500'}`}
                            ></div>
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Forecasting Chart */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-base font-bold text-gray-900 mb-1">{t('30-Day Revenue Trend & Projections')}</h2>
                    <p className="text-xs text-gray-500 mb-4">{t('Daily revenue history combined with 30-day moving average trend.')}</p>
                    <div className="h-72">
                        <Line data={generateForecastChart()} options={{ maintainAspectRatio: false, interaction: { mode: 'index', intersect: false } }} />
                    </div>
                </div>

                {/* Branch KPIs */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-base font-bold text-gray-900 mb-1">{t('Cross-Branch Comparison')}</h2>
                    <p className="text-xs text-gray-500 mb-4">{t('Side-by-side revenue, expenses, and net performance across depots.')}</p>
                    <div className="h-72">
                         <Bar data={generateBranchChart()} options={{ 
                             maintainAspectRatio: false, 
                             scales: { 
                                 y: { grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { color: '#64748b' } },
                                 x: { grid: { display: false }, ticks: { color: '#64748b' } }
                             },
                             plugins: { legend: { position: 'top', labels: { boxWidth: 12, font: { size: 11 } } } }
                         }} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AnalyticsDashboard;
